import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { UploadDraftProvider } from "@/contexts/UploadDraftContext";
import { ViewModeProvider } from "@/contexts/ViewModeContext";
import { PublicSettingsProvider } from "@/hooks/usePublicSettings";
import { Toaster } from "sonner";

import Layout from "@/components/colourmatch/Layout";
import Landing from "@/pages/Landing";
import GetStarted from "@/pages/GetStarted";
import SharedInvite from "@/pages/SharedInvite";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Projects from "@/pages/Projects";
import VisualiserNew from "@/pages/VisualiserNew";
import Workspace from "@/pages/Workspace";
import Pricing from "@/pages/Pricing";
import UnlockSuccess from "@/pages/UnlockSuccess";
import Account from "@/pages/Account";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminPainterDetail from "@/pages/AdminPainterDetail";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refunds from "@/pages/Refunds";
import Contact from "@/pages/Contact";
import HowItWorks from "@/pages/HowItWorks";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import QuoteForm from "@/pages/QuoteForm";
import QRPage from "@/pages/QRPage";
import OwnerQuickTest from "@/pages/OwnerQuickTest";
import PainterApply from "@/pages/PainterApply";
import OwnerRedirectToPainter from "@/components/OwnerRedirectToPainter";
import PaintersDirectory from "@/pages/PaintersDirectory";
import PaintersJoin from "@/pages/PaintersJoin";
import PainterPublicProfile from "@/pages/PainterPublicProfile";
import PainterLayout from "@/pages/painter/PainterLayout";  // kept on disk for rollback safety, not routed
import PainterOverview from "@/pages/painter/PainterOverview";  // kept on disk for rollback safety, not routed
import PainterProfile from "@/pages/painter/PainterProfile";
import PainterGallery from "@/pages/painter/PainterGallery";
import PainterAreas from "@/pages/painter/PainterAreas";
import PainterCredentials from "@/pages/painter/PainterCredentials";
import PainterLeads from "@/pages/painter/PainterLeads";
import AllColours from "@/pages/AllColours";
import HelpCentre from "@/pages/HelpCentre";
import PainterSharedVisualisation from "@/pages/painter/PainterSharedVisualisation";
import PainterComingSoon from "@/pages/painter/PainterComingSoon";  // kept on disk for rollback safety, not routed
import PainterQuotesList from "@/pages/painter/PainterQuotesList";
import QuoteBuilder from "@/pages/painter/QuoteBuilder";
import HomeownerQuoteView from "@/pages/HomeownerQuoteView";

// Membership V1 · Phase 1
import MembershipApply from "@/pages/painter/MembershipApply";
import AdminMembershipApplications from "@/pages/admin/AdminMembershipApplications";
import AdminMembershipApplicationDetail from "@/pages/admin/AdminMembershipApplicationDetail";
import AdminVerificationMonitor from "@/pages/admin/AdminVerificationMonitor";
// Membership V1 · Phase 3
import PainterMembership from "@/pages/painter/PainterMembership";
import AdminPricingConfig from "@/pages/admin/AdminPricingConfig";
// Membership V1 · Phase 4
import PainterJoinPage from "@/pages/PainterJoinPage";
import MyReferrals from "@/pages/MyReferrals";
import AdminReferrals from "@/pages/admin/AdminReferrals";

// ColourMatch Pro — legacy workspace shell (kept on disk for rollback safety, not routed)
import ProLayout from "@/pages/pro/ProLayout";  // kept on disk for rollback safety, not routed
import ProCmsHome from "@/pages/pro/ProCmsHome";
import ProProjectDetail from "@/pages/pro/ProProjectDetail";
import PainterSettings from "@/pages/pro/PainterSettings";

// ColourMatch Pro · Job Workflow MVP (behind JOB_WORKFLOW_ENABLED flag).
import ProJobsList from "@/pages/pro/ProJobsList";
import ProJobDetail from "@/pages/pro/ProJobDetail";

// Owner-only Deploy Preflight (mandatory pre-deploy gate).
import AdminDeployPreflight from "@/pages/admin/AdminDeployPreflight";

// ColourMatch Pro · Phase 5 — public homeowner share view (no login).
import SharedProjectPage from "@/pages/pro/SharedProjectPage";
// ColourMatch Pro · Milestone 4 — public homeowner presentation view (no login).
import SharedPresentation from "@/pages/pro/SharedPresentation";
// ColourMatch Pro · Redesign 2026-02-09 — unified professional shell.
import ColourMatchProShell from "@/pages/pro/ColourMatchProShell";
import ProDashboard from "@/pages/pro/ProDashboard";
import BusinessProfile from "@/pages/pro/BusinessProfile";
import PainterBranding from "@/pages/pro/PainterBranding";
import PublicProfile from "@/pages/pro/PublicProfile";
import ProRendersLanding from "@/pages/pro/ProRendersLanding";
import ProPresentationsList from "@/pages/pro/ProPresentationsList";
import ProRegister from "@/pages/pro/ProRegister";

// Stage 1A v2 design-review mockups (static — no backend)
import Stage1AIndex from "@/pages/mockups/Stage1AIndex";
import PresetGalleryMockup from "@/pages/mockups/PresetGalleryMockup";
import SchemeBuilderMockup from "@/pages/mockups/SchemeBuilderMockup";
import CompareModeMockup from "@/pages/mockups/CompareModeMockup";
import PaletteCardMockup from "@/pages/mockups/PaletteCardMockup";
import PaintSpecificationMockup from "@/pages/mockups/PaintSpecificationMockup";

function Protected({ children, admin = false }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="p-10 text-center text-sm text-brand-muted">Loading…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  // Note: admin-only routes render their child component, which is expected to show its own
  // "Admin access required" guard for non-admin users. The role check lives in the child so the
  // user sees an explicit denied message instead of being silently redirected.
  return children;
}

// Legacy redirects — preserve every old painter URL by rewriting it into
// the new ColourMatch Pro route table (see COLOURMATCH_PRO_LOCKED.md).
function LegacyQuoteRedirect() {
  const { id } = useParams();
  return <Navigate to={`/pro/quotes/${id}`} replace />;
}
function LegacyProjectRedirect() {
  const { id } = useParams();
  return <Navigate to={`/pro/projects/${id}`} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <UploadDraftProvider>
          <ViewModeProvider>
            <PublicSettingsProvider>
            <Toaster position="top-right" richColors />
            <Layout>
              <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/get-started" element={<GetStarted />} />
              <Route path="/shared-invite/:token" element={<SharedInvite />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refunds" element={<Refunds />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/colours" element={<AllColours />} />
              <Route path="/help" element={<HelpCentre />} />
              {/* Homeowner-share entry — public, token-scoped (no login). */}
              <Route path="/shared/presentation/:token" element={<SharedPresentation />} />
              <Route path="/shared/:token" element={<SharedProjectPage />} />
              <Route path="/shared/:token/schemes/:schemeId" element={<SharedProjectPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* QR funnel — /quote is the printed-QR landing target. Public, mobile-first. */}
              <Route path="/quote" element={<QuoteForm />} />
              <Route path="/painters" element={<PaintersDirectory />} />
              <Route path="/painters/join" element={<PaintersJoin />} />
              <Route path="/painters/apply" element={<OwnerRedirectToPainter><PainterApply /></OwnerRedirectToPainter>} />
              <Route path="/quotes/view/:token" element={<HomeownerQuoteView />} />
              <Route path="/pro/join/:token" element={<PainterJoinPage />} />
              <Route path="/referrals" element={<Protected><MyReferrals /></Protected>} />
              <Route path="/painters/:slug" element={<PainterPublicProfile />} />
              {/* ColourMatch Pro · Lightweight registration (public, no login required). */}
              <Route path="/pro/register" element={<ProRegister />} />
              {/* -----------------------------------------------------------
                  ColourMatch Pro · unified professional workspace
                  (Redesign 2026-02-09 — see COLOURMATCH_PRO_LOCKED.md).
                  All old /painter/* and /pro/cms/* URLs redirect into
                  this shell. See legacy-redirects block below.
                  ----------------------------------------------------------- */}
              <Route path="/pro" element={<Protected><ColourMatchProShell /></Protected>}>
                <Route index element={<ProDashboard />} />
                <Route path="projects" element={<ProCmsHome />} />
                <Route path="projects/:id" element={<ProProjectDetail />} />
                <Route path="renders" element={<ProRendersLanding />} />
                <Route path="presentations" element={<ProPresentationsList />} />
                {/* /pro/proposals is the locked customer-facing name for
                    Visualisation Proposals (2026-02 platform direction).
                    Points at the same list; sidebar links use /pro/presentations
                    to keep existing bookmarks live. */}
                <Route path="proposals" element={<Navigate to="/pro/presentations" replace />} />
                <Route path="quotes" element={<PainterQuotesList />} />
                <Route path="quotes/new" element={<QuoteBuilder />} />
                <Route path="quotes/:id" element={<QuoteBuilder />} />
                <Route path="leads" element={<PainterLeads />} />
                <Route path="leads/shared/:token" element={<PainterSharedVisualisation />} />
                <Route path="jobs" element={<ProJobsList />} />
                <Route path="jobs/:id" element={<ProJobDetail />} />
                <Route path="business" element={<BusinessProfile />}>
                  <Route index element={<Navigate to="details" replace />} />
                  <Route path="details" element={<PainterProfile />} />
                  <Route path="branding" element={<PainterBranding />} />
                  <Route path="gallery" element={<PainterGallery />} />
                  <Route path="areas" element={<PainterAreas />} />
                  <Route path="credentials" element={<PainterCredentials />} />
                  <Route path="public" element={<PublicProfile />} />
                </Route>
                <Route path="billing" element={<PainterMembership />} />
                <Route path="billing/activated" element={<PainterMembership />} />
                <Route path="billing/apply" element={<MembershipApply />} />
                <Route path="billing/apply/done" element={<MembershipApply />} />
                <Route path="settings" element={<PainterSettings />} />
              </Route>

              {/* -----------------------------------------------------------
                  Legacy route redirects — every old painter surface points
                  into the new ColourMatch Pro shell. Keep in perpetuity so
                  bookmarks, email links and shared URLs never break.
                  ----------------------------------------------------------- */}
              <Route path="/painter" element={<Navigate to="/pro" replace />} />
              <Route path="/painter/profile" element={<Navigate to="/pro/business/details" replace />} />
              <Route path="/painter/leads" element={<Navigate to="/pro/leads" replace />} />
              <Route path="/painter/quotes" element={<Navigate to="/pro/quotes" replace />} />
              <Route path="/painter/quotes/new" element={<Navigate to="/pro/quotes/new" replace />} />
              <Route path="/painter/quotes/:id" element={<LegacyQuoteRedirect />} />
              <Route path="/painter/gallery" element={<Navigate to="/pro/business/gallery" replace />} />
              <Route path="/painter/areas" element={<Navigate to="/pro/business/areas" replace />} />
              <Route path="/painter/credentials" element={<Navigate to="/pro/business/credentials" replace />} />
              <Route path="/painter/colour-library" element={<Navigate to="/pro/projects" replace />} />
              <Route path="/painter/analytics" element={<Navigate to="/pro" replace />} />
              <Route path="/painter/membership" element={<Navigate to="/pro/billing" replace />} />
              <Route path="/painter/membership/activated" element={<Navigate to="/pro/billing/activated" replace />} />
              <Route path="/painter/membership/apply" element={<Navigate to="/pro/billing/apply" replace />} />
              <Route path="/painter/membership/apply/done" element={<Navigate to="/pro/billing/apply/done" replace />} />
              <Route path="/pro/cms" element={<Navigate to="/pro/projects" replace />} />
              <Route path="/pro/cms/projects/:id" element={<LegacyProjectRedirect />} />
              {/* /qr is the internal poster generator (used to print QR cards). */}
              <Route path="/qr" element={<QRPage />} />
              <Route path="/projects" element={<Protected><Projects /></Protected>} />
              <Route path="/visualiser/new" element={<Protected><VisualiserNew /></Protected>} />
              <Route path="/visualiser/p/:id" element={<Protected><Workspace /></Protected>} />
              <Route path="/unlock/success" element={<Protected><UnlockSuccess /></Protected>} />
              <Route path="/account" element={<Protected><Account /></Protected>} />
              <Route path="/admin" element={<Protected admin><AdminDashboard /></Protected>} />
              <Route path="/admin/painters/:id" element={<Protected admin><AdminPainterDetail /></Protected>} />
              <Route path="/admin/membership-applications" element={<Protected admin><AdminMembershipApplications /></Protected>} />
              <Route path="/admin/membership-applications/:id" element={<Protected admin><AdminMembershipApplicationDetail /></Protected>} />
              <Route path="/admin/membership/verification-monitor" element={<Protected admin><AdminVerificationMonitor /></Protected>} />
              <Route path="/admin/membership/pricing" element={<Protected admin><AdminPricingConfig /></Protected>} />
              <Route path="/admin/referrals" element={<Protected admin><AdminReferrals /></Protected>} />
              <Route path="/admin/deploy-preflight" element={<Protected admin><AdminDeployPreflight /></Protected>} />
              {/* Hidden owner-only shortcut — not linked from nav. Provisions a fresh unlocked test project. */}
              <Route path="/owner/quick-test" element={<Protected><OwnerQuickTest /></Protected>} />
              {/* Stage 1A v2 design-review mockups — Phase-1 Commercial
                  Polish (2026-07-04) gated to non-production builds only.
                  Prevents accidental public exposure of unfinished design
                  work at /mockups/*. The routes still function in local
                  dev + preview (where NODE_ENV=development). */}
              {process.env.NODE_ENV !== "production" && (
                <>
                  <Route path="/mockups/stage-1a" element={<Stage1AIndex />} />
                  <Route path="/mockups/preset-gallery" element={<PresetGalleryMockup />} />
                  <Route path="/mockups/scheme-builder" element={<SchemeBuilderMockup />} />
                  <Route path="/mockups/compare-mode" element={<CompareModeMockup />} />
                  <Route path="/mockups/palette-card" element={<PaletteCardMockup />} />
                  <Route path="/mockups/paint-specification" element={<PaintSpecificationMockup />} />
                </>
              )}              {/* Catch-all: redirect old paths home */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
            </PublicSettingsProvider>
          </ViewModeProvider>
          </UploadDraftProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
